<script>
import axios from 'axios';
import resolveUrl from '@/services/resolveUrl';

/**
 * AdicionarAGrupoModal — adiciona um contato (pelo telefone) a um grupo de
 * WhatsApp direto do grid de contatos.
 *
 * Lista só os grupos "acionáveis": onde o número do canal é ADMIN e ainda é
 * participante (fora disso o WhatsApp recusa a adição). Cada linha chama
 * POST /grupo/adicionarMembro/:id — que valida o número, resolve o JID e
 * devolve um motivo amigável quando não dá (privacidade, já é membro, etc).
 */
export default {
  name: 'AdicionarAGrupoModal',
  props: {
    visivel: { type: Boolean, default: false },
    // { id, nome, telefone } — telefone já em dígitos (DDI+DDD).
    contato: { type: Object, default: null },
  },
  emits: ['fechar'],
  data() {
    return {
      grupos: [],
      carregando: false,
      erro: null,
      busca: '',
      // Por grupo: id do grupo em processamento e o resultado da tentativa.
      adicionandoId: null,
      resultados: {}, // { [grupoId]: { ok, mensagem } }
    };
  },
  computed: {
    contatoNome() {
      return (this.contato && (this.contato.nome || `Contato #${this.contato.id}`)) || '';
    },
    telefone() {
      return String((this.contato && this.contato.telefone) || '').replace(/\D/g, '');
    },
    gruposFiltrados() {
      const termo = this.busca.trim().toLowerCase();
      if (!termo) return this.grupos;
      return this.grupos.filter((g) => (g.nome || '').toLowerCase().includes(termo));
    },
  },
  watch: {
    visivel(v) {
      if (v) this._abrir();
    },
  },
  mounted() {
    if (this.visivel) this._abrir();
  },
  methods: {
    _abrir() {
      this.busca = '';
      this.resultados = {};
      this.adicionandoId = null;
      this.erro = null;
      this.carregarGrupos();
    },
    fechar() {
      if (this.adicionandoId) return;
      this.$emit('fechar');
    },
    resolveAssetUrl(relativeUrl) {
      if (!relativeUrl) return '';
      if (/^(https?:|data:)/i.test(relativeUrl)) return relativeUrl;
      const base = resolveUrl(process.env.VUE_APP_APIURL) || '';
      return `${base.replace(/\/$/, '')}${relativeUrl}`;
    },
    async carregarGrupos() {
      this.carregando = true;
      this.erro = null;
      try {
        const params = {
          order: JSON.stringify([['nome', 'asc']]),
          limit: 500,
          include: JSON.stringify([{ model: 'CanalBaileys', as: 'canal' }]),
        };
        const { data } = await axios.get('/grupo', { params });
        const lista = Array.isArray(data) ? data : [];
        // Só grupos onde dá pra agir: número do canal é admin, ainda é
        // participante, não arquivado e não é o nó pai de comunidade.
        const acionaveis = lista.filter((g) => (
          g.is_admin && g.participante_atual !== false && !g.arquivado && !g.eh_comunidade
        ));
        // O mesmo grupo pode ter várias linhas (uma por canal). Deduplica por
        // jid mantendo a primeira (todas já são admin aqui).
        const vistos = new Set();
        this.grupos = acionaveis.filter((g) => {
          if (vistos.has(g.jid)) return false;
          vistos.add(g.jid);
          return true;
        });
      } catch (error) {
        this.erro = `Falha ao listar grupos: ${error.response?.data?.error || error.message}`;
      } finally {
        this.carregando = false;
      }
    },
    async adicionar(grupo) {
      if (this.adicionandoId) return;
      if (!this.telefone) {
        this.resultados = { ...this.resultados, [grupo.id]: { ok: false, mensagem: 'Este contato não tem telefone cadastrado.' } };
        return;
      }
      this.adicionandoId = grupo.id;
      // Limpa resultado anterior deste grupo (permite retry).
      const copia = { ...this.resultados };
      delete copia[grupo.id];
      this.resultados = copia;
      try {
        const { data } = await axios.post(`/grupo/adicionarMembro/${grupo.id}`, { telefone: this.telefone });
        this.resultados = {
          ...this.resultados,
          [grupo.id]: {
            ok: Boolean(data && data.ok),
            mensagem: (data && data.mensagem) || (data && data.ok ? 'Adicionado ao grupo.' : 'Não foi possível adicionar.'),
          },
        };
        if (data && data.ok) {
          // Reflete a nova contagem localmente (best-effort).
          grupo.participantes_count = (grupo.participantes_count || 0) + 1;
        }
      } catch (error) {
        this.resultados = {
          ...this.resultados,
          [grupo.id]: {
            ok: false,
            mensagem: error.response?.data?.mensagem || error.response?.data?.error || error.message,
          },
        };
      } finally {
        this.adicionandoId = null;
      }
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <div v-if="visivel" class="modal-backdrop-custom" @click.self="fechar">
      <div class="modal-card shadow-lg">
        <header class="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
          <h6 class="m-0 text-truncate">
            <i class="ri-group-line me-1"></i>Adicionar a um grupo
          </h6>
          <button type="button" class="btn btn-link p-0 text-body" :disabled="!!adicionandoId" @click="fechar">
            <i class="ri-close-line" style="font-size: 1.4rem;"></i>
          </button>
        </header>

        <div class="modal-body-custom p-3">
          <p class="small text-muted mb-2">
            Adicionar <strong class="text-body">{{ contatoNome }}</strong> a um grupo de WhatsApp.
          </p>

          <div v-if="!telefone" class="alert alert-warning py-2 small mb-2">
            <i class="ri-error-warning-line me-1"></i>
            Este contato não tem telefone cadastrado — não é possível adicioná-lo a um grupo.
          </div>

          <div class="search-box mb-2">
            <input
              v-model="busca"
              type="text"
              class="form-control form-control-sm"
              placeholder="Buscar grupo pelo nome..."
            />
          </div>

          <div v-if="carregando" class="text-center py-4 text-muted">
            <i class="ri-loader-4-line spin me-1"></i>carregando grupos...
          </div>

          <div v-else-if="erro" class="alert alert-danger py-2 small mb-0">
            <i class="ri-error-warning-line me-1"></i>{{ erro }}
          </div>

          <div v-else-if="!gruposFiltrados.length" class="text-center text-muted py-4 small">
            <i class="ri-group-line d-block mb-2" style="font-size: 1.8rem; opacity: 0.4;"></i>
            <template v-if="busca">Nenhum grupo encontrado para "{{ busca }}".</template>
            <template v-else>
              Nenhum grupo disponível. Só é possível adicionar em grupos onde o
              número do canal é <strong>administrador</strong>.
            </template>
          </div>

          <ul v-else class="list-group lista-grupos">
            <li
              v-for="g in gruposFiltrados"
              :key="g.id"
              class="list-group-item d-flex align-items-center gap-2"
            >
              <span class="grupo-avatar flex-shrink-0">
                <img v-if="g.imagem_url" :src="resolveAssetUrl(g.imagem_url)" alt="" @error="$event.target.remove()" />
                <i v-else class="ri-group-line"></i>
              </span>
              <div class="flex-grow-1 min-w-0">
                <div class="fw-semibold text-truncate">{{ g.nome || '(sem nome)' }}</div>
                <small class="text-muted">
                  <i class="ri-user-3-line"></i> {{ g.participantes_count }}
                  <span v-if="g.canal && g.canal.nome"> · {{ g.canal.nome }}</span>
                </small>
                <div v-if="resultados[g.id]" class="small mt-1" :class="resultados[g.id].ok ? 'text-success' : 'text-danger'">
                  <i :class="resultados[g.id].ok ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'" class="me-1"></i>
                  {{ resultados[g.id].mensagem }}
                </div>
              </div>
              <button
                type="button"
                class="btn btn-sm flex-shrink-0"
                :class="resultados[g.id] && resultados[g.id].ok ? 'btn-success' : 'btn-outline-success'"
                :disabled="!telefone || !!adicionandoId || (resultados[g.id] && resultados[g.id].ok)"
                @click="adicionar(g)"
              >
                <i v-if="adicionandoId === g.id" class="ri-loader-4-line spin"></i>
                <i v-else-if="resultados[g.id] && resultados[g.id].ok" class="ri-check-line"></i>
                <template v-else><i class="ri-add-line me-1"></i>Adicionar</template>
              </button>
            </li>
          </ul>
        </div>

        <footer class="d-flex justify-content-end gap-2 px-3 py-2 border-top">
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="!!adicionandoId" @click="fechar">
            Fechar
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-card {
  background: var(--vz-body-bg);
  color: var(--vz-body-color);
  border-radius: 0.5rem;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}
.modal-body-custom {
  overflow-y: auto;
  flex: 1;
}
.lista-grupos {
  max-height: 50vh;
  overflow-y: auto;
}
.min-w-0 {
  min-width: 0;
}
.grupo-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--vz-tertiary-bg, var(--vz-light, #f3f6f9));
  color: var(--vz-secondary-color, #878a99);
  font-size: 1.2rem;
}
.grupo-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
